const features = [
  {
    icon: 'ri-rocket-line',
    title: 'Lightning-Fast Delivery',
    description: 'From concept to deployment in weeks, not months',
  },
  {
    icon: 'ri-building-line',
    title: 'Enterprise-Ready Architecture',
    description: 'Scalable, secure, and built for mission-critical operations',
  },
  {
    icon: 'ri-cloud-line',
    title: 'Hybrid Cloud + Local',
    description: 'Deploy anywhere: cloud, on-premise, or hybrid infrastructure',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Security-First',
    description: 'Bank-grade encryption and security protocols',
  },
  {
    icon: 'ri-file-shield-line',
    title: 'GDPR + KVKK Compliant',
    description: 'Full compliance with international data protection regulations',
  },
  {
    icon: 'ri-brain-line',
    title: 'Agentic AI + Workflow AI Expertise',
    description: 'Cutting-edge AI agents that think and act autonomously',
  },
];

export default function WhySection() {
  return (
    <section className="relative py-32 px-6 bg-gradient-to-b from-[#0a0a0f] to-[#1a0b2e]">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, rgba(139, 92, 246, 0.3) 1px, transparent 0)`,
            backgroundSize: '40px 40px',
            animation: 'backgroundScroll 20s linear infinite',
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-20">
          <h2 className="text-5xl md:text-6xl font-black mb-6">
            <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-cyan-400 bg-clip-text text-transparent">
              Why Automate.TR?
            </span>
          </h2>
          <p className="text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            We don't just automate processes.
            <br />
            <span className="text-purple-400 font-bold">We transform businesses.</span>
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
          {features.map((feature, index) => (
            <div
              key={index}
              className="group relative"
              style={{
                animation: `fadeInUp 0.6s ease-out ${index * 0.1}s backwards`,
              }}
            >
              <div className="relative h-full p-8 rounded-2xl border border-purple-500/20 bg-gradient-to-br from-purple-900/10 to-transparent backdrop-blur-sm hover:border-purple-400/40 transition-all duration-300 cursor-pointer">
                {/* Icon */}
                <div className="mb-6">
                  <div className="w-16 h-16 flex items-center justify-center rounded-xl bg-gradient-to-br from-purple-500 to-cyan-500 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
                    <i className={`${feature.icon} text-3xl text-white`} />
                  </div>
                </div>

                {/* Content */}
                <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-purple-400 group-hover:to-cyan-400 group-hover:bg-clip-text transition-all duration-300">
                  {feature.title}
                </h3>
                <p className="text-gray-400 leading-relaxed">{feature.description}</p>

                {/* Hover Glow */}
                <div className="absolute inset-0 bg-gradient-to-br from-purple-500/0 to-cyan-500/0 group-hover:from-purple-500/5 group-hover:to-cyan-500/5 rounded-2xl transition-all duration-300 pointer-events-none" />
              </div>
            </div>
          ))}
        </div>

        {/* Stats Section */}
        <div className="relative">
          <div
            className="relative p-12 rounded-3xl border border-purple-500/30 overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(59, 130, 246, 0.05))',
            }}
          >
            {/* Background Glow */}
            <div
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-gradient-to-br from-purple-500/20 to-cyan-500/20 blur-3xl"
            />

            {/* Stats Grid */}
            <div className="relative grid grid-cols-1 md:grid-cols-4 gap-8">
              {[
                { value: '500+', label: 'Workflows Automated' },
                { value: '98%', label: 'Client Satisfaction' },
                { value: '10M+', label: 'Tasks Processed' },
                { value: '24/7', label: 'System Uptime' },
              ].map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-5xl md:text-6xl font-black bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent mb-2">
                    {stat.value}
                  </div>
                  <div className="text-gray-400 font-semibold">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes backgroundScroll {
          from {
            transform: translateY(0);
          }
          to {
            transform: translateY(40px);
          }
        }
      `}</style>
    </section>
  );
}
